'use client'

import type { ReactNode } from 'react'

import { PICKED_FILL } from '@/lib/surface'
import { cn } from '@/lib/utils'

/**
 * One answer in a pill group: a slug for the link, a word for the eye.
 *
 * `short` is that word again for somewhere narrow — the running summary of a
 * choice, where "Od 3 do 5 osob" would eat the line the date has to share. It
 * is optional: an answer that is already one word is its own short form.
 */
export type Choice = { value: string; label: string; short?: string; detail?: string }

/**
 * How many people are involved — in front of the lens on a shoot, around the
 * table at a planning meeting.
 *
 * `3-plus` is a BUCKET, not a number, and that is deliberate: this page prices
 * nothing, so the exact head count is a question for the brief. What the bucket
 * has to do is tell the team whether they are covering one person, two, or a
 * group — which is the difference between one light and a crew.
 */
/**
 * Answers laid out in even rows, INCLUDING the row that does not divide.
 *
 * The track is six units wide because six divides by one, two and three, so
 * every row can be filled whatever the answer count is: five answers across
 * three columns become 2+2+2 and then 3+3 — two full rows — where a plain
 * `grid-cols-3` would leave a third of the last row empty, which is the ragged
 * edge this replaced in the first place.
 *
 * One column narrower on a phone, because the answers do not shrink with the
 * screen: „Nieruchomości" in a third of 375px is clipped mid-word and „Od 11 do
 * 20 produktów" in half of it breaks in two while its neighbour keeps one line.
 */
const GRID_TRACK = 'grid grid-cols-6'

/**
 * How tall one row of answers may grow to, in rem.
 *
 * 7rem fills most of a 530px panel with two or three rows and still leaves the
 * question room to breathe. Past it the group centres in what is left: „fill
 * the height" taken literally turns six answers into six 200px lozenges, which
 * is a poster, not a question.
 */
const MAX_ROW = 7

/** How many of the six units one answer takes, on a phone and above `sm`. */
function spanOf(columns: 2 | 3, count: number, index: number) {
  const isLast = index === count - 1
  // A phone shows half of what a laptop does: one answer per row at two
  // columns, two at three. The odd one out takes the whole row either way.
  const phone = columns === 2 || (count % 2 === 1 && isLast) ? 'col-span-6' : 'col-span-3'

  if (columns === 2) return cn(phone, count % 2 === 1 && isLast ? 'sm:col-span-6' : 'sm:col-span-3')

  const rest = count % 3
  if (rest === 1 && isLast) return cn(phone, 'sm:col-span-6')
  if (rest === 2 && index >= count - 2) return cn(phone, 'sm:col-span-3')
  return cn(phone, 'sm:col-span-2')
}

export const PEOPLE_CHOICES: readonly Choice[] = [
  { value: '1', label: '1', short: '1 os.' },
  { value: '2', label: '2', short: '2 os.' },
  { value: '3-plus', label: '3+', short: '3+ os.' },
]

/**
 * A captioned row of one-word answers, unchoosable back to nothing.
 *
 * Clicking the chosen pill clears it, the same as the plans and the cities
 * above — none of these questions is compulsory, and a control a visitor can
 * enter but not leave is how an accidental tap ends up in an enquiry as a
 * stated requirement.
 */
export function PillGroup({
  label,
  choices,
  value,
  onChange,
  action,
  columns,
  fill,
  sticky,
  className,
}: {
  label: string
  choices: readonly Choice[]
  value: string | null
  onChange: (next: string | null) => void
  /**
   * Something to put at the far end of the caption row — one control, no more.
   *
   * `/pl/fotograf-operator` puts an „ⓘ co to za sesje?" there. It belongs on
   * the caption rather than on each pill for a reason that is structural, not
   * aesthetic: a pill IS a button, and a button inside a button is not valid
   * HTML — the nested one never receives a click in some browsers and the
   * accessibility tree reports something nobody meant.
   */
  action?: ReactNode
  /**
   * Lay the answers out as equal columns filling the row, instead of letting
   * them wrap at whatever width their own word happens to be.
   *
   * Free wrapping is right when the answers are one character long. It is
   * wrong for the six kinds of session, where "Kobieca" and "Nieruchomosci"
   * differ by a factor of two and the row below starts wherever the row above
   * ran out — a ragged edge inside a panel whose every other control (the
   * plans, the cities) runs the full width of it.
   */
  columns?: 2 | 3
  /**
   * Let the answers grow into the height they are given, not just the width.
   *
   * Owner: *„te pytania i miasta muszą być dopasowane do szerokości i wysokości
   * kafelka"*. One question alone in a 460px panel used to sit at its own
   * height with the rest of the panel empty under it.
   *
   * Capped at `MAX_ROW` per row, because „fill the height" taken literally
   * makes six answers into six 200px lozenges — a poster, not a question. Up to
   * the cap they stretch; past it the group centres in what is left, which is
   * what a short question in a tall panel should do.
   */
  fill?: boolean
  /**
   * Pressing the chosen answer re-confirms it instead of taking it back.
   *
   * „Unchoosable back to nothing" is right for a question standing on its own.
   * On a path it is a trap: the answer IS the way forward, so somebody who
   * pressed „Wróć" to re-read what they picked and then pressed it again to
   * carry on would have their answer deleted and the panel would not move.
   * Nothing is lost by it — a different answer still replaces this one, and
   * „Wróć" still leads out.
   */
  sticky?: boolean
  className?: string
}) {
  return (
    // Caption ABOVE the pills but on the same flow as them, at the tight
    // `clamp` the rest of this column uses. It used to be a caption, a 12px
    // gap and a row — 30px of structure per question, twice over, on a screen
    // the three columns have to share. What it is not is a caption to the LEFT
    // of the pills: "Ile osób przed obiektywem?" is longer than the three
    // answers it introduces, so the pills would have started two thirds of the
    // way across a column that is already a third of the page.
    <div className={cn(fill && 'flex min-h-0 flex-1 flex-col justify-center', className)}>
      <div className="flex shrink-0 items-center justify-between gap-3">
        <p className="text-[0.75rem] font-semibold uppercase tracking-[0.18em] text-white/55">
          {label}
        </p>
        {action}
      </div>
      <div
        className={cn(
          'mt-[clamp(0.35rem,1svh,0.75rem)] gap-[clamp(0.3rem,0.9svh,0.5rem)]',
          columns ? GRID_TRACK : 'flex flex-wrap',
          fill && columns ? 'min-h-0 flex-1 auto-rows-fr' : null,
        )}
        style={
          fill && columns
            ? { maxHeight: `${Math.ceil(choices.length / columns) * MAX_ROW}rem` }
            : undefined
        }
      >
        {choices.map((choice, index) => {
          const isOn = value === choice.value
          return (
            <button
              key={choice.value}
              type="button"
              onClick={() => onChange(isOn && !sticky ? null : choice.value)}
              aria-pressed={isOn}
              className={cn(
                'border py-[clamp(0.3rem,0.9svh,0.5rem)] text-[0.875rem] font-semibold transition',
                fill ? 'rounded-2xl' : 'rounded-full',
                // In a column the pill already has its width; the padding only
                // has to keep the word off the border, and px-4 is what makes
                // "Nieruchomosci" break onto a second line on a narrow laptop.
                columns ? 'min-w-0 px-2 text-center' : 'px-4',
                fill ? 'h-full' : null,
                columns ? spanOf(columns, choices.length, index) : null,
                isOn
                  ? PICKED_FILL
                  : 'border-white/15 bg-white/[0.04] text-white/75 hover:border-white/35 hover:text-white',
              )}
            >
              {choice.label}
              {/* A second line only where an answer is a package rather than a
                word — what it holds and what it costs, so the choice is made
                on the content and not on the name. */}
              {choice.detail ? (
                <span
                  className={cn(
                    'mt-0.5 block text-[0.75rem] leading-snug font-medium',
                    isOn ? 'opacity-80' : 'text-white/55',
                  )}
                >
                  {choice.detail}
                </span>
              ) : null}
            </button>
          )
        })}
      </div>
    </div>
  )
}
